import React from 'react';
import { Link } from 'react-router-dom';
import { Youtube, Music2, Cloud, HardDrive, ArrowRight, Sparkles, Clock, Disc, ListMusic } from 'lucide-react';

/** /migrate — Migration Center hub.
 * Single landing for every "bring your catalog over" path.
 * Today: YouTube channel migration. Soon: Spotify catalog mirror, Bandcamp, SoundCloud.
 */
export default function Migrate() {
  return (
    <div className="max-w-[1100px] mx-auto" data-testid="migrate-page">
      <header className="mb-7">
        <div className="chip"><Sparkles className="w-3 h-3" /> Migration Center</div>
        <h1 className="font-display text-[36px] sm:text-[48px] md:text-[56px] mt-3 leading-[1.02]">Bring your <span className="brand-gradient">whole catalog</span> over.</h1>
        <p className="text-white/65 text-[14px] md:text-[16px] mt-3 max-w-[640px] leading-relaxed">
          One-click migrations from the platforms you already use. Keep your fans, keep your back catalog, keep your video work — without re-uploading any of it.
        </p>
      </header>

      <section className="grid sm:grid-cols-2 gap-3" data-testid="migrate-tile-grid">
        <MigrationTile
          to="/migrate/youtube"
          status="live"
          Icon={Youtube}
          color="#ff0033"
          title="YouTube channel"
          desc="Mirror your video catalog into VidCreForge. Imports titles, descriptions, thumbnails. Free."
          cta="Start YouTube migration"
        />
        <MigrationTile
          status="soon"
          Icon={Music2}
          color="#1DB954"
          title="Spotify artist catalog"
          desc="Mirror your Spotify discography into your ReaperCast Artists profile. Auto-syncs follower counts."
          cta="Coming Q2 · join waitlist"
        />
        <MigrationTile
          status="soon"
          Icon={Disc}
          color="#629aa9"
          title="Bandcamp"
          desc="Move your Bandcamp store, fan list, and back catalog. Preserves discography ordering + tags."
          cta="Coming Q2 · join waitlist"
        />
        <MigrationTile
          status="soon"
          Icon={Cloud}
          color="#ff5500"
          title="SoundCloud"
          desc="One-click import of your tracks, descriptions, and follower count. Stems supported."
          cta="Coming Q2 · join waitlist"
        />
        <MigrationTile
          status="soon"
          Icon={ListMusic}
          color="#ffcf52"
          title="Distributor catalog (DistroKid / TuneCore / CD Baby)"
          desc="Pull your distributor's release feed and create matching ReaperCast artist tracks."
          cta="Coming Q3"
        />
        <MigrationTile
          status="soon"
          Icon={HardDrive}
          color="#a78bfa"
          title="Bulk file import (R2 / S3 / Drive)"
          desc="Mass-upload an entire folder via a signed URL. Auto-extracts metadata + matches to releases."
          cta="Coming Q3"
        />
      </section>

      <section className="card-shell p-5 mt-6 flex items-start gap-3" style={{ borderColor: 'rgba(167,139,250,0.3)', background: 'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(34,211,238,0.04))' }}>
        <Clock className="w-5 h-5 text-[#a78bfa] mt-0.5" />
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#a78bfa]">Why migrate to ReaperCast?</div>
          <p className="text-[13px] text-white/70 mt-1 leading-relaxed">Every account ships with <span className="text-white font-semibold">1 GB of free video hosting</span>, the Music Biz Academy, AI specialists, and the Knowledge Hub. You only pay if you need more storage — your music + the platform stay free.</p>
        </div>
      </section>
    </div>
  );
}

function MigrationTile({ to, status, Icon, color, title, desc, cta }) {
  const tile = (
    <div className="card-shell p-5 hover-lift h-full flex flex-col" data-testid={`migrate-tile-${title.toLowerCase().split(' ')[0]}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: color + '22', color }}><Icon className="w-5 h-5" /></div>
        <span className="chip text-[10px]" style={{ color: status === 'live' ? '#34d399' : '#94a3b8', borderColor: (status === 'live' ? '#34d399' : '#94a3b8') + '55' }}>
          {status === 'live' ? 'LIVE' : 'COMING SOON'}
        </span>
      </div>
      <h3 className="font-display text-[16px] mt-1">{title}</h3>
      <p className="text-[12.5px] text-white/60 mt-1.5 leading-relaxed flex-1">{desc}</p>
      <div className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: status === 'live' ? color : '#94a3b8' }}>
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </div>
  );
  return to ? <Link to={to}>{tile}</Link> : <div className="cursor-not-allowed">{tile}</div>;
}

// Note: lucide-react does not export "Hardrive" — use HardDrive.
